import { Api } from '@automapa/svelte-automapa-template';
import { z } from 'zod';
import {
    AppOptionsSchema,
    type AppOptionsType,
    AutodispatchOutputSchema__DEPRECATED,
    type AutodispatchOutputType__DEPRECATED,
} from './EasyVrpApp.types';

// {"id":17,"geom":{"crs":{"type":"name","properties":{"name":"EPSG:4326"}},"type":"Point","coordinates":[20.239043351212818,52.22879461978842]},"color":"#96de7e","count":812,"source":"primary","cell_id":93}
export const CalcNucleiResponseSchema = z.object({
    id: z.number().describe('vehicle id'),
    geom: z.object({
        crs: z.object({
            type: z.literal('name'),
            properties: z.object({
                name: z.literal('EPSG:4326'),
            }),
        }),
        type: z.literal('Point'),
        coordinates: z.array(z.number()).length(2),
    }),
    color: z.string(),
    count: z.number(),
    source: z.string().describe('primary or secondary'),
    cell_id: z.number(),
});
export type CalcNucleiResponseType = z.infer<typeof CalcNucleiResponseSchema>;

export const AutodispatchResultsRequestSchema = z.object({
    id: z.number(),
});
export type AutodispatchResultsRequestType = z.infer<typeof AutodispatchResultsRequestSchema>;

export const AutodispatchResultsResponseSchema = z.object({
    id: z.number(),
    result: AutodispatchOutputSchema__DEPRECATED.nullable().catch(null),
    // request_id: z.number().describe('DEPRECATED - use id instead'),
    status: z.string().describe('completed | pending | not-found | error'),
    message: z.string(),
});
export type AutodispatchResultsResponseType = z.infer<typeof AutodispatchResultsResponseSchema>;

export const CalcAutodispatchResponseSchema = z.object({
    // request_id: z.number(),
    id: z.number().describe('ID of the vrp calculation'),
    message: z.string(),
});
export type CalcAutodispatchResponseType = z.infer<typeof CalcAutodispatchResponseSchema>;

export const CountPointsResponseSchema = z.object({
    historical_jobs: z.number(),
    unassigned_jobs: z.number(),
});
export type CountPointsResponseType = z.infer<typeof CountPointsResponseSchema>;

export const CalculationIdInProgressSchema = z.object({
    id: z.number(),
    progress_float: z.number().describe('Progress of the calculation as a float between 0 and 1'),
});
export type CalculationIdInProgressType = z.infer<typeof CalculationIdInProgressSchema>;

export class EasyVrpApi {
    static async getCountPoints(
        api: Api,
        appOptions: AppOptionsType
    ): Promise<CountPointsResponseType> {
        return api.getData<any, CountPointsResponseType>(
            'post',
            `/easy_vrp/get_count_points`,
            appOptions,
            AppOptionsSchema,
            CountPointsResponseSchema
        );
    }

    static async createHexes(api: Api): Promise<any> {
        return api.getData<any, any>('post', `/easy_vrp/create_hexes`, {}, z.any(), z.any());
    }

    static async calculateNuclei(
        api: Api,
        options: AppOptionsType
    ): Promise<CalcNucleiResponseType[]> {
        return api.getData<AppOptionsType, CalcNucleiResponseType[]>(
            'post',
            `/easy_vrp/calculate_nuclei`,
            options,
            AppOptionsSchema,
            z.array(CalcNucleiResponseSchema)
        );
    }

    static async calculateAutodispatch(
        api: Api,
        options: AppOptionsType
    ): Promise<CalcAutodispatchResponseType> {
        return api.getData<AppOptionsType, CalcAutodispatchResponseType>(
            'post',
            `/easy_vrp/calculate_autodispatch`,
            options,
            AppOptionsSchema,
            CalcAutodispatchResponseSchema
        );
    }

    static async getAutodispatchResults(
        api: Api,
        id: number
    ): Promise<AutodispatchResultsResponseType> {
        return api.getData<AutodispatchResultsRequestType, AutodispatchResultsResponseType>(
            'post',
            `/easy_vrp/get_autodispatch_results`,
            { id: id },
            AutodispatchResultsRequestSchema,
            AutodispatchResultsResponseSchema
        );
    }

    static async removeHistoryJobs(api: Api): Promise<any> {
        return api.getData<any, any>('post', '/easy_vrp/remove_history_jobs', {}, z.any(), z.any());
    }

    static async getCalculationIdsInProgress(api: Api): Promise<CalculationIdInProgressType[]> {
        return api.getData<any, CalculationIdInProgressType[]>(
            'post',
            '/easy_vrp/get_calculation_ids_in_progress',
            {},
            z.any(),
            z.array(CalculationIdInProgressSchema)
        );
    }

    static assignCalculation(api: Api, calc_id: number, selected_day: string) {
        return api.getData<{ calc_id: number; selected_day: string }, any>(
            'post',
            '/easy_vrp/assign_calculation',
            { calc_id, selected_day },
            z.object({
                calc_id: z.number(),
                selected_day: z.string(),
            }),
            z.any()
        );
    }

    static checkAllUnfinishedCalculations(api: Api): Promise<any> {
        return api.getData<any, any>(
            'post',
            '/easy_vrp/check_all_unfinished_calculations',
            {},
            z.any(),
            z.any()
        );
    }
}
